import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { todayLocal } from '../../../utils/format';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  CircularProgress,
  Autocomplete,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  useCreateRelacionLaboral,
  useUpdateRelacionLaboral,
} from '../../../hooks/useRelacionesLaborales';
import {
  useSucursales,
  useCategorias,
  useEmpresasActivas,
} from '../../../hooks/useSucursalesYCategorias';
import type { RelacionLaboralExpandida } from '../../../types/relacionLaboral';

// #031: trim + max + validación cruzada fecha_baja > fecha_alta + no futuras
const hoyIso = () => todayLocal();

const schema = z
  .object({
    empresa_id: z
      .string({ required_error: 'Empresa es requerida' })
      .uuid('Empresa es requerida — seleccionala de la lista'),
    sucursal_id: z.string().uuid().nullable().optional(),
    categoria_id: z.string().uuid().nullable().optional(),
    fecha_alta: z
      .string({ required_error: 'Fecha de alta es requerida' })
      .min(1, 'Fecha de alta es requerida')
      .refine(
        (v) => v <= hoyIso(),
        'Fecha de alta no puede ser futura',
      ),
    fecha_baja: z
      .string()
      .refine(
        (v) => v === '' || v <= hoyIso(),
        'Fecha de baja no puede ser futura',
      )
      .optional()
      .or(z.literal('')),
    estado: z.enum(['activa', 'baja']),
    observaciones: z
      .string()
      .trim()
      .max(500, 'Observaciones no pueden superar 500 caracteres')
      .optional()
      .or(z.literal('')),
  })
  .refine(
    (v) => !v.fecha_baja || v.fecha_baja >= v.fecha_alta,
    {
      message: 'La fecha de baja debe ser igual o posterior a la fecha de alta',
      path: ['fecha_baja'],
    },
  )
  .refine(
    (v) => v.estado === 'activa' || !!v.fecha_baja,
    {
      message: 'Para dar de baja debés ingresar la fecha de baja',
      path: ['fecha_baja'],
    },
  );

type FormValues = z.infer<typeof schema>;

const defaultValues: FormValues = {
  empresa_id: '',
  sucursal_id: null,
  categoria_id: null,
  fecha_alta: todayLocal(),
  fecha_baja: '',
  estado: 'activa',
  observaciones: '',
};

interface Props {
  open: boolean;
  afiliadoId: string;
  relacion: RelacionLaboralExpandida | null;
  onClose: () => void;
}

export default function RelacionLaboralDialog({
  open,
  afiliadoId,
  relacion,
  onClose,
}: Props) {
  const isEdit = !!relacion;
  const create = useCreateRelacionLaboral();
  const update = useUpdateRelacionLaboral();

  const { data: empresas = [], isLoading: loadingEmpresas } = useEmpresasActivas();
  const { data: categorias = [] } = useCategorias();

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const empresaId = watch('empresa_id');
  const { data: sucursales = [], isLoading: loadingSucursales } = useSucursales(
    empresaId || null
  );

  useEffect(() => {
    if (open) {
      reset(
        relacion
          ? {
              empresa_id: relacion.empresa_id,
              sucursal_id: relacion.sucursal_id,
              categoria_id: relacion.categoria_id,
              fecha_alta: relacion.fecha_alta,
              fecha_baja: relacion.fecha_baja ?? '',
              estado: relacion.estado,
              observaciones: relacion.observaciones ?? '',
            }
          : defaultValues
      );
    }
  }, [open, relacion, reset]);

  const onSubmit = async (values: FormValues) => {
    const payload = {
      afiliado_id: afiliadoId,
      empresa_id: values.empresa_id,
      sucursal_id: values.sucursal_id || null,
      categoria_id: values.categoria_id || null,
      fecha_alta: values.fecha_alta,
      fecha_baja: values.fecha_baja || null,
      estado: values.estado,
      observaciones: values.observaciones || null,
    };
    try {
      if (isEdit && relacion) {
        // En edit, no permitimos cambiar afiliado_id ni empresa_id
        const { afiliado_id, empresa_id, ...editPayload } = payload;
        void afiliado_id;
        void empresa_id;
        await update.mutateAsync({ id: relacion.id, payload: editPayload });
      } else {
        await create.mutateAsync(payload);
      }
      onClose();
    } catch {
      // se muestra abajo
    }
  };

  const apiError = (isEdit ? update.error : create.error) as Error | null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogTitle>
          {isEdit ? 'Editar relacion laboral' : 'Nueva relacion laboral'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {apiError && <Alert severity="error">{apiError.message}</Alert>}

            <Controller
              name="empresa_id"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  value={empresas.find((e) => e.id === field.value) ?? null}
                  onChange={(_, opt) => {
                    field.onChange(opt?.id ?? '');
                    // Limpiar sucursal al cambiar de empresa
                    setValue('sucursal_id', null);
                  }}
                  options={empresas}
                  getOptionLabel={(opt) => `${opt.razon_social} (${opt.cuit})`}
                  loading={loadingEmpresas}
                  disabled={isEdit}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label="Empresa"
                      error={!!errors.empresa_id}
                      helperText={
                        errors.empresa_id?.message ??
                        (isEdit ? 'No se puede cambiar la empresa' : undefined)
                      }
                    />
                  )}
                />
              )}
            />

            <Controller
              name="sucursal_id"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  value={sucursales.find((s) => s.id === field.value) ?? null}
                  onChange={(_, opt) => field.onChange(opt?.id ?? null)}
                  options={sucursales}
                  getOptionLabel={(opt) =>
                    `${opt.denominacion ?? '(sin denominacion)'}${
                      opt.casa_central ? ' — Casa central' : ''
                    }${opt.domicilio ? ` — ${opt.domicilio}` : ''}`
                  }
                  loading={loadingSucursales}
                  disabled={!empresaId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sucursal"
                      placeholder={
                        empresaId ? 'Buscar sucursal...' : 'Seleccione una empresa primero'
                      }
                    />
                  )}
                />
              )}
            />

            <Controller
              name="categoria_id"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  value={categorias.find((c) => c.id === field.value) ?? null}
                  onChange={(_, opt) => field.onChange(opt?.id ?? null)}
                  options={categorias}
                  getOptionLabel={(opt) =>
                    opt.codigo ? `${opt.codigo} — ${opt.descripcion}` : opt.descripcion
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Categoria" placeholder="Buscar..." />
                  )}
                />
              )}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                {...register('fecha_alta')}
                type="date"
                required
                fullWidth
                label="Fecha de alta"
                InputLabelProps={{ shrink: true }}
                error={!!errors.fecha_alta}
                helperText={errors.fecha_alta?.message}
              />
              <TextField
                {...register('fecha_baja')}
                type="date"
                fullWidth
                label="Fecha de baja"
                InputLabelProps={{ shrink: true }}
                error={!!errors.fecha_baja}
                helperText={errors.fecha_baja?.message ?? 'Dejar vacio si sigue vigente'}
              />
            </Stack>

            <Controller
              name="estado"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value === 'activa'}
                      onChange={(e) => field.onChange(e.target.checked ? 'activa' : 'baja')}
                    />
                  }
                  label={field.value === 'activa' ? 'Relacion activa' : 'Relacion dada de baja'}
                />
              )}
            />

            <TextField
              {...register('observaciones')}
              fullWidth
              multiline
              rows={2}
              label="Observaciones"
              error={!!errors.observaciones}
              helperText={errors.observaciones?.message}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {isSubmitting ? 'Guardando...' : isEdit ? 'Guardar' : 'Crear relacion'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
